import React, { useCallback, useEffect, useState } from 'react';
import type { InvoiceDto } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { Icon } from '../icons';
import { euro } from '../format';
import { Empty, ErrorBanner, Panel, Stat } from '../ui';
import { ask } from '../dialogs';

interface Row {
  invoice: InvoiceDto;
  issuerName: string | null;
  iban: string | null;
}

const dateIt = (d: string | null) => (d ? new Date(d.length === 10 ? d + 'T12:00:00' : d).toLocaleDateString('it-IT') : '—');
const today = () => new Date().toISOString().slice(0, 10);
const dueOf = (i: InvoiceDto) => i.dueDate || (i.issueDate ? new Date(new Date(i.issueDate + 'T12:00:00Z').getTime() + 30 * 86_400_000).toISOString().slice(0, 10) : null);

/** Pagamenti (cliente): cosa c'è da pagare, con coordinate bancarie e causale già pronte. */
export function PaymentsPage({ ctx }: { ctx: Ctx }) {
  const { api } = ctx;
  const [rows, setRows] = useState<Row[] | null>(null);
  const [paid, setPaid] = useState<InvoiceDto[]>([]);
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await api.get<Row[]>('/api/client/payments'));
      setPaid((await api.get<InvoiceDto[]>('/api/client/invoices')).filter((i) => i.status === 'paid'));
    } catch (e) {
      setError((e as Error).message);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (ctx.event?.type === 'app.notice') void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.eventSeq]);

  const copy = async (text: string, key: string) => {
    await navigator.clipboard.writeText(text).catch(() => undefined);
    setCopied(key);
    setTimeout(() => setCopied((c) => (c === key ? null : c)), 2000);
  };

  const notify = async (i: InvoiceDto) => {
    if (!(await ask(`Segnalare di aver pagato la fattura ${i.number}?`))) return;
    try {
      await api.post(`/api/client/invoices/${i.id}/paid-notice`);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const total = (rows ?? []).reduce((n, r) => n + r.invoice.totalCents, 0);
  const late = (rows ?? []).filter((r) => (dueOf(r.invoice) ?? '9999') < today()).reduce((n, r) => n + r.invoice.totalCents, 0);

  return (
    <div className="panels single">
      <Panel title="Pagamenti" subtitle="Le fatture da pagare, con i dati per il bonifico">
        <ErrorBanner message={error} onClose={() => setError(null)} />
        {rows && (
          <div className="stats-row" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <Stat value={euro(total)} label="Da pagare" navy />
            <Stat value={euro(late)} label="Di cui scaduto" />
            <Stat value={paid.length} label="Fatture pagate" />
          </div>
        )}
        {!rows ? (
          <div className="muted small">Caricamento…</div>
        ) : rows.length === 0 ? (
          <Empty icon="check">Nessun pagamento in sospeso.</Empty>
        ) : (
          <div className="ag-list">
            {rows.map(({ invoice: i, issuerName, iban }) => {
              const due = dueOf(i);
              const isLate = !!due && due < today();
              const causale = `Fattura ${i.number}`;
              return (
                <div key={i.id} className="pay-card">
                  <div className="row-actions" style={{ justifyContent: 'space-between', margin: 0 }}>
                    <b>
                      Fattura {i.number} · {euro(i.totalCents)}
                    </b>
                    <span className={isLate ? 'kan-due late' : 'muted small'}>Scadenza {dateIt(due)}</span>
                  </div>
                  <div className="muted small">{i.subject}</div>
                  {i.lastReminderAt && <div className="small">Promemoria inviato il {dateIt(i.lastReminderAt)}</div>}
                  {iban ? (
                    <div className="pay-bank">
                      <div className="muted small">Bonifico a {issuerName}</div>
                      <div className="pay-line">
                        <span className="mono">{iban}</span>
                        <button type="button" className="btn small" onClick={() => void copy(iban, `i${i.id}`)}>
                          <Icon name="copy" size={13} /> {copied === `i${i.id}` ? 'Copiato' : 'Copia IBAN'}
                        </button>
                      </div>
                      <div className="pay-line">
                        <span>
                          Causale: <b>{causale}</b>
                        </span>
                        <button type="button" className="btn small" title="Copia la causale del bonifico" onClick={() => void copy(causale, `c${i.id}`)}>
                          <Icon name="copy" size={13} /> {copied === `c${i.id}` ? 'Copiato' : 'Copia'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="muted small">Coordinate bancarie non indicate: chiedi al tuo consulente.</div>
                  )}
                  <div className="row-actions" style={{ margin: 0 }}>
                    {i.clientPaidNoticeAt ? (
                      <span className="small">Pagamento segnalato il {dateIt(i.clientPaidNoticeAt)}: in attesa di conferma</span>
                    ) : (
                      <button type="button" className="btn small" title="Avvisa che hai pagato: il consulente controlla e conferma" onClick={() => void notify(i)}>
                        <Icon name="check" size={13} /> Ho pagato
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {paid.length > 0 && (
          <>
            <h3 style={{ margin: '22px 0 8px' }}>Pagate</h3>
            <table className="table">
              <tbody>
                {paid.map((i) => (
                  <tr key={i.id}>
                    <td>{i.number}</td>
                    <td className="muted">{i.subject}</td>
                    <td className="muted">{dateIt(i.paidAt)}</td>
                    <td className="num">{euro(i.totalCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </Panel>
    </div>
  );
}
